import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MenuCaret } from './MenuCaret';

/**
 * Content → Articles: one "Topic" dropdown (Prototype v7) in place of a chip
 * per topic, which a feed with a thousand topics turned into a wall. It lists
 * the top 8 topics by article count ("Top 8 of N"), searches all of them, and
 * shows topics that differ only by case ("NEWS" / "News") as one; picking it
 * filters by every spelling.
 */
export interface TopicGroup {
  key: string;
  label: string;
  names: string[];
  count: number;
}

const TOP = 8;

/** Topics merged by case; labelled by their most-used spelling, most articles first. */
export function groupTopics(
  categories: Array<{ name: string; count: number }>,
  label: (name: string) => string
): TopicGroup[] {
  const groups = new Map<
    string,
    { names: Array<{ name: string; count: number }>; count: number }
  >();
  for (const c of categories) {
    const key = c.name.trim().toLowerCase();
    if (!key) continue;
    const g = groups.get(key) || { names: [], count: 0 };
    g.names.push(c);
    g.count += c.count;
    groups.set(key, g);
  }
  return [...groups.entries()]
    .map(([key, g]) => {
      const top = [...g.names].sort((a, b) => b.count - a.count)[0];
      return { key, label: label(top.name), names: g.names.map((n) => n.name), count: g.count };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function TopicFilter({
  groups,
  allCount,
  value,
  onChange,
}: {
  groups: TopicGroup[];
  /** Articles in every topic, for "All topics" (blank until known). */
  allCount?: number | null;
  /** The chosen group's key, or null for all topics. */
  value: string | null;
  onChange: (group: TopicGroup | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrap = useRef<HTMLSpanElement>(null);
  const listId = useId();
  const chosen = groups.find((g) => g.key === value) || null;

  const needle = q.trim().toLowerCase();
  const shown = useMemo(
    () =>
      needle ? groups.filter((g) => g.label.toLowerCase().includes(needle)) : groups.slice(0, TOP),
    [groups, needle]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const pick = (g: TopicGroup | null) => {
    onChange(g);
    setOpen(false);
    setQ('');
  };

  return (
    <span className="ink-topic-filter" ref={wrap}>
      <button
        type="button"
        className="ink-chip"
        data-active={chosen ? 'true' : 'false'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        Topic: {chosen ? chosen.label : 'All'} <MenuCaret />
      </button>
      {open ? (
        <div className="ink-topic-panel">
          <input
            className="ink-topic-search"
            type="search"
            placeholder="Search topics"
            aria-label="Search topics"
            value={q}
            autoFocus
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && shown[0]) pick(shown[0]);
            }}
          />
          <div role="listbox" id={listId} aria-label="Topics">
            {!needle ? (
              <button
                type="button"
                role="option"
                aria-selected={!chosen}
                data-active={!chosen ? 'true' : 'false'}
                onClick={() => pick(null)}
              >
                <span>All topics</span>
                <span>{allCount != null ? allCount.toLocaleString() : ''}</span>
              </button>
            ) : null}
            {shown.map((g) => (
              <button
                key={g.key}
                type="button"
                role="option"
                aria-selected={g.key === value}
                data-active={g.key === value ? 'true' : 'false'}
                onClick={() => pick(g)}
              >
                <span>{g.label}</span>
                <span>{g.count.toLocaleString()}</span>
              </button>
            ))}
            {needle && !shown.length ? (
              <p className="ink-topic-note">No topic matches “{q.trim()}”.</p>
            ) : null}
          </div>
          {!needle && groups.length > TOP ? (
            <p className="ink-topic-note">
              Top {TOP} of {groups.length.toLocaleString()} · search for the rest
            </p>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}
